import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Save, Info } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

export default function SmsTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({});

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sms-templates`, { withCredentials: true });
      setTemplates(data);
      const nextDrafts = {};
      data.forEach((t) => {
        nextDrafts[t.template_key] = { message: t.message, is_active: t.is_active };
      });
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error('SMS şablonları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const updateDraft = (key, patch) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSave = async (key) => {
    setSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const draft = drafts[key];
      await axios.put(`${API}/admin/sms-templates/${key}`, {
        message: draft.message,
        is_active: draft.is_active,
      }, { withCredentials: true });
      toast.success('Şablon güncellendi');
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const isDirty = (t) => {
    const d = drafts[t.template_key];
    if (!d) return false;
    return d.message !== t.message || d.is_active !== t.is_active;
  };

  const smsLength = (msg) => (msg || '').length;
  const smsParts = (msg) => Math.max(1, Math.ceil(smsLength(msg) / 160));

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sms-templates-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-600" />
          SMS Şablonları
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Sistemden giden tüm SMS mesajlarının içeriğini düzenleyin veya kapatın.
          Kapalı olan şablon için SMS gönderilmez.
        </p>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p><strong>Değişken kullanımı:</strong> Her şablonun kendi değişkenleri vardır (örn. <code>{'{name}'}</code>, <code>{'{facility_name}'}</code>). Değişkenleri süslü parantez içinde kullanın; sistem otomatik olarak gerçek değerlerle değiştirir.</p>
            <p className="mt-1"><strong>SMS karakter limiti:</strong> Tek SMS 160 karakter, Türkçe karakterler (ş, ğ, ç, ı, ö, ü) SMS&apos;i 70 karaktere düşürebilir. Karakter sayacına dikkat edin.</p>
          </div>
        </CardContent>
      </Card>

      {templates.map((t) => {
        const draft = drafts[t.template_key] || { message: t.message, is_active: t.is_active };
        const dirty = isDirty(t);
        const length = smsLength(draft.message);
        const parts = smsParts(draft.message);

        return (
          <Card key={t.template_key} data-testid={`sms-template-${t.template_key}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-lg text-slate-900">{t.name}</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={draft.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                    {draft.is_active ? 'Aktif' : 'Pasif'}
                  </Badge>
                  <Switch
                    checked={draft.is_active}
                    onCheckedChange={(v) => updateDraft(t.template_key, { is_active: v })}
                    data-testid={`sms-template-active-${t.template_key}`}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {t.variables && t.variables.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500">Kullanılabilir değişkenler:</span>
                  {t.variables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const cur = draft.message || '';
                        updateDraft(t.template_key, { message: cur + '{' + v + '}' });
                      }}
                      className="px-2 py-0.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-xs font-mono text-slate-700"
                    >
                      {`{${v}}`}
                    </button>
                  ))}
                </div>
              )}
              <Textarea
                value={draft.message}
                onChange={(e) => updateDraft(t.template_key, { message: e.target.value })}
                rows={4}
                className="font-mono text-sm"
                data-testid={`sms-template-message-${t.template_key}`}
              />
              <div className="flex items-center justify-between text-xs">
                <div className={length > 160 ? 'text-amber-600' : 'text-slate-500'}>
                  {length} karakter · {parts} SMS parçası
                </div>
                <Button
                  onClick={() => handleSave(t.template_key)}
                  disabled={!dirty || saving[t.template_key]}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  size="sm"
                  data-testid={`sms-template-save-${t.template_key}`}
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving[t.template_key] ? 'Kaydediliyor...' : (dirty ? 'Kaydet' : 'Kayıtlı')}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
